import { Component, Input, OnInit } from '@angular/core';
import { Folder, FolderRoleEnum, FolderShare, FolderShareFragment } from '@app/models';
import { AppService, ErrorHandlerService, FolderService, NotyService, WhitelabelService } from '@app/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FoldersListingService } from '../folders-listing.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize, of } from 'rxjs';
import { LoaderService } from '@app/shared';

export type ModalResult = FolderShareFragment[];

export interface ModalPublicProperties {
  folder: Folder;
}

export enum ShareContext {
  People = 'people',
  Groups = 'groups',
  Link = 'link'
}

@Component({
  selector: 'app-share-folder-modal',
  templateUrl: './share-folder-modal.component.html',
  styleUrl: './share-folder-modal.component.scss',
  standalone: false
})
export class ShareFolderModalComponent implements OnInit {
  @Input() folder: Folder;
  @Input() organizationId?: number;

  context = ShareContext.People;
  tabOptions = [
    [ShareContext.People, this.translateService.instant('documents.shareFolder.people')],
    [ShareContext.Groups, this.translateService.instant('documents.shareFolder.groups')],
    [ShareContext.Link, this.translateService.instant('documents.shareFolder.link')]
  ];
  search = '';
  accessLink = '';
  linkForm!: UntypedFormGroup;
  isPasswordRequired = false;
  searchedShares: Folder['shares'] = null;

  readonly ShareContext = ShareContext;
  readonly FolderRoleEnum = FolderRoleEnum;

  constructor(
    public modal: NgbActiveModal,
    private translateService: TranslateService,
    private folderService: FolderService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private foldersListingService: FoldersListingService,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private appService: AppService,
    private whitelabelService: WhitelabelService
  ) {
    this.linkForm = this.formBuilder.group({
      requirePassword: false,
      password: ''
    });
  }

  ngOnInit() {
    this.accessLink = null;
    if (!!this.folder.link_identifier) {
      this.setAccessLink(this.folder.link_identifier);

      this.linkForm.patchValue({
        requirePassword: this.folder.has_password
      });
    }
  }

  changeShareRole(share: FolderShare, role: FolderRoleEnum) {
    if (share.role === role) return;
    if (role === FolderRoleEnum.Viewer && this.folder.shares.map(share => share.role).filter(role => role === FolderRoleEnum.Editor).length <= 1) {
      this.notyService.error(this.translateService.instant('error_cannot_remove_last_editor'));
      return;
    }

    this.folderService.updateSharing({ folderId: this.folder.id, role, sharingId: share.id }).subscribe({
      next: folder => {
        this.folder.shares = folder.shares;
        this.notyService.success(this.translateService.instant('documents.shareFolder.sharingRoleUpdated'));
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  addSharing(isGroup = false) {
    this.foldersListingService.openAddFolderSharingModal({ folder: this.folder, isGroup, organizationId: this.organizationId }).subscribe({
      next: shares => (this.folder.shares = shares)
    });
  }

  removeShare(share: FolderShare) {
    this.folderService.removeFolderSharing({ folderId: this.folder.id, sharingId: share.id }).subscribe({
      next: () => {
        const index = this.folder.shares.indexOf(share);
        this.folder.shares = this.folder.shares.filter((_, i) => i !== index);

        this.notyService.success(this.translateService.instant('documents.shareFolder.accessRemoved'));
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  setAccessLink(linkId: string) {
    const whitelabel$ = !this.appService.isAutentiqueDomain() ? this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' }) : of(null);
    whitelabel$.subscribe({
      next: whitelabel => {
        const baseDomain = whitelabel?.domain ?? 'app.autentique.com.br';
        this.accessLink = `https://${baseDomain}/f/${linkId}`;
      }
    });
  }

  copyAccessLink() {
    this.notyService.success(this.translateService.instant('documents.shareFolder.accessLinkCopied'));
  }

  searchShares(context: ShareContext) {
    if (this.search.trim() === '' || context === ShareContext.Link) {
      this.searchedShares = null;
      return;
    }
    this.searchedShares = this.folder.shares.filter(share => {
      if (context === ShareContext.People) {
        if (share.user?.email?.toLowerCase().includes(this.search.toLowerCase())) return true;
      }
      const name = context === ShareContext.People ? share.user?.name : context === ShareContext.Groups ? share.group?.name : '';
      return (name || '').toLowerCase().includes(this.search.toLowerCase());
    });
  }

  updateAccessLink(isRevoke = false) {
    this.loaderService.show();
    this.folderService
      .updateSharing({ folderId: this.folder.id, shareByLink: !isRevoke })
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: folder => {
          const identifier = folder.link_identifier;
          try {
            this.folder.link_identifier = identifier;
          } catch (e) {
            if (e.message.includes('read only')) {
              this.folder = folder;
            }
          }
          isRevoke ? (this.accessLink = null) : this.setAccessLink(identifier);
          this.notyService.success(this.translateService.instant(`documents.shareFolder.${isRevoke ? 'accessLinkRevoked' : 'accessLinkGenerated'}`));
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  updateFolderPassword() {
    if (this.linkForm.invalid) {
      this.linkForm.markAllAsTouched();
      return;
    }

    this.loaderService.show();

    const { requirePassword, password } = this.linkForm.getRawValue();
    const passwordData = requirePassword ? { password } : { removePassword: true };

    this.folderService
      .updateSharing({ folderId: this.folder.id, ...passwordData, shareByLink: true })
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: folder => {
          this.folder.has_password = folder.has_password;
          this.notyService.success(this.translateService.instant('documents.shareFolder.passwordUpdated'));
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }
}
